import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, Loader2, Wallet } from "lucide-react";
import { toast } from "sonner";
import { useRefData } from "@/context/RefDataContext";
import { useAuth } from "@/context/AuthContext";

function fmtINR(n) {
  if (n == null || isNaN(n)) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n);
}
function fmtDate(s) {
  if (!s) return "—";
  return new Date(s).toLocaleDateString(undefined, { dateStyle: "medium" });
}

export default function WalletDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const ref = useRefData();
  const { user: me } = useAuth();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  // Back-link target: if this is the current user's own wallet, go to the
  // "My Wallet" tab; otherwise go to the "Team Wallets" tab.
  const backHref =
    doc && me && doc.user_id === me.id ? "/my-wallet" : "/my-wallet?tab=team";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/wallets/${id}`);
      setDoc(data);
    } catch (e) {
      toast.error("Wallet not found");
      navigate("/my-wallet?tab=team");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="flex items-center gap-2 text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading wallet…</div>;
  }
  if (!doc) return null;

  const taggedUser = (ref.users || []).find((u) => u.value === doc.user_id);
  const ledger = doc.ledger || [];
  const unsettledTotal = doc.unsettled_total || 0;

  return (
    <div className="space-y-8" data-testid="wallet-detail-page">
      <Link to={backHref} className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white" data-testid="wallet-detail-back-link">
        <ArrowLeft className="w-4 h-4" /> Back to Wallets
      </Link>

      <header className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-overline mb-2 flex items-center gap-2">
            <Wallet className="w-3.5 h-3.5" /> Wallet
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-white" style={{ fontFamily: "Outfit" }} data-testid="wallet-detail-holder">
            {doc.holder_name}
          </h1>
          <div className="flex items-center gap-3 mt-3 flex-wrap text-xs text-zinc-500">
            <span className="font-mono text-emerald-400" data-testid="wallet-detail-number">{doc.wallet_number}</span>
            {taggedUser && <span>Tagged: <span className="text-zinc-300">{taggedUser.label}</span></span>}
            <span>Code: <span className="font-mono text-zinc-300">{doc.code}</span></span>
          </div>
        </div>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-lg border border-white/10 bg-zinc-900 p-4">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500">Opening Balance</div>
          <div className="text-2xl font-semibold font-mono text-zinc-100 mt-1">{fmtINR(doc.opening_balance)}</div>
        </div>
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
          <div className="text-[11px] uppercase tracking-wider text-amber-300">Unsettled Fund</div>
          <div className="text-2xl font-semibold font-mono text-amber-300 mt-1" data-testid="wallet-detail-unsettled">{fmtINR(unsettledTotal)}</div>
          <div className="text-[11px] text-zinc-500 mt-0.5">across open OPAs</div>
        </div>
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4">
          <div className="text-[11px] uppercase tracking-wider text-emerald-300">Closing Balance</div>
          <div className="text-2xl font-semibold font-mono text-emerald-300 mt-1" data-testid="wallet-detail-closing">{fmtINR(doc.closing_balance)}</div>
        </div>
      </section>

      <section className="bg-zinc-900 border border-white/10 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10 text-[11px] uppercase tracking-wider text-zinc-500 font-medium flex items-center justify-between">
          <span>Wallet Ledger</span>
          <span className="font-mono text-amber-300 normal-case tracking-normal text-xs">{fmtINR(unsettledTotal)} unsettled</span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-950/50 border-b border-white/10 text-left">
              {["Date", "Reference Number", "Narration", "Debit", "Credit", "Balance"].map((c, i) => (
                <th key={i} className="px-4 py-3 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ledger.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-zinc-500 text-sm">No wallet entries yet.</td></tr>
            ) : ledger.map((e, idx) => (
              <tr key={idx} className="border-b border-white/5 hover:bg-white/[0.025]" data-testid={`wallet-ledger-row-${idx}`}>
                <td className="px-4 py-3 text-zinc-400 text-xs">{fmtDate(e.date)}</td>
                <td className="px-4 py-3 font-mono text-xs text-sky-300">{e.reference}</td>
                <td className="px-4 py-3 text-zinc-300 text-xs">{e.narration}</td>
                <td className="px-4 py-3 font-mono text-rose-300">{e.debit ? fmtINR(e.debit) : <span className="text-zinc-700">—</span>}</td>
                <td className="px-4 py-3 font-mono text-emerald-300">{e.credit ? fmtINR(e.credit) : <span className="text-zinc-700">—</span>}</td>
                <td className="px-4 py-3 font-mono text-white">{fmtINR(e.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-4 py-2.5 border-t border-white/10 text-[11px] text-zinc-500">
          OPA payment → Credit. Reimbursement → Debit. Settlement clears the difference. Unsettled total represents OPAs still in lifecycle.
        </div>
      </section>
    </div>
  );
}
